import { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { emitHomePageViewGAEvent } from 'utils/ga-event-helpers';

// SF-29: emit `home_page_view` once per home/landing-page mount, deferred until the
// current-session GA4 User-ID is available so the event — and the `session_start` /
// `first_visit` GA4 derives from the session's first event — carries the SAME `user_id`
// as `search_submission`, letting the view→search funnel join on user_id.
//
// The id (uid/imid) comes from Redux `state.base`, populated when `init('flights')`
// resolves. It is NOT redux-persisted, so it is always THIS session's id. We deliberately
// do NOT read the persisted `tpdrops-uid` / `tpdrops-imid` from localStorage: uid/imid
// rotate per session, so the stored value is a STALE previous-session id that would
// mismatch search_submission's current id (BigQuery: ~74% of returning-user sessions) and
// break the join.
//
// A 2s timeout guarantees the event still fires (without user_id) if `init` is slow or
// fails — the funnel event is never dropped. The ref de-dupes the emit (e.g. React
// StrictMode's double-invoke in dev).
export const useHomePageViewGAEvent = () => {
	const uid = useSelector((state: { base: { uid: string | null } }) => state.base.uid);
	const imid = useSelector((state: { base: { imid: string | null } }) => state.base.imid);
	const firedRef = useRef(false);

	useEffect(() => {
		if (firedRef.current) return;

		const fire = () => {
			if (firedRef.current) return;
			firedRef.current = true;
			// Attach the current-session User-ID (built exactly like baseActions.init's set)
			// so home_page_view carries the same user_id as search_submission.
			const userId = [uid, imid].filter(Boolean).join('') || undefined;
			if (userId && typeof window !== 'undefined' && typeof window.gtag === 'function') {
				window.gtag('set', { user_id: userId });
			}
			emitHomePageViewGAEvent();
		};

		// Defer on the current-session `uid` from Redux (populated by init()) for ALL users —
		// NOT the persisted localStorage id, which rotates per session and would be stale.
		if (uid) {
			fire();
			return;
		}

		// init() slow or failed → fire without user_id rather than dropping the funnel event.
		const timeoutId = setTimeout(fire, 2000);
		return () => clearTimeout(timeoutId);
	}, [uid, imid]);
};
